// hand/positions.ts - seat ↔ position mapping for 2–9 players, incl. heads-up blind reversal.
// ENGINE_DESIGN.md §2.1 ("heads-up blind reversal"). POSITION_ORDER is already in preflop-action
// order (UTG..BB), so for n>=3 the last n entries ARE the n-handed preflop order; heads-up (n=2)
// is a documented special case where the button plays SB and posts first preflop, last postflop.
import { POSITION_ORDER } from '@postflop/schema';
/** The n-handed subset of POSITION_ORDER (n=2..9), already in preflop action order. */
export const truncatedPositions = (n) => {
    if (n < 2 || n > 9)
        throw new RangeError(`table size must be 2-9, got ${n}`);
    return POSITION_ORDER.slice(POSITION_ORDER.length - n);
};
/** seat -> Position for an n-handed table with the button at `buttonSeat`. Seats are 0..n-1. */
export const assignPositions = (n, buttonSeat) => {
    const positions = truncatedPositions(n);
    const map = new Map();
    if (n === 2) {
        map.set(buttonSeat, 'SB');
        map.set((buttonSeat + 1) % n, 'BB');
        return map;
    }
    // index i (0=first-to-act .. n-1=BB) sits at seat (buttonSeat + 3 + i) mod n.
    for (let i = 0; i < n; i++) {
        const seat = (buttonSeat + 3 + i) % n;
        map.set(seat, positions[i]);
    }
    return map;
};
export const seatForPosition = (positions, position) => {
    for (const [seat, pos] of positions)
        if (pos === position)
            return seat;
    return undefined;
};
/** Preflop action order (first-to-act .. last, BB/option closes). Heads-up: SB(button) then BB. */
export const preflopOrder = (n, buttonSeat) => {
    if (n === 2)
        return [buttonSeat, (buttonSeat + 1) % 2];
    return Array.from({ length: n }, (_, i) => (buttonSeat + 3 + i) % n);
};
/** Postflop action order: SB first, BTN last - EXCEPT heads-up, where it reverses (BB first,
 *  button/SB last), since the button/SB is not "left of the blinds" in a 2-seat table. */
export const postflopOrder = (n, buttonSeat) => {
    if (n === 2)
        return [(buttonSeat + 1) % 2, buttonSeat];
    return Array.from({ length: n }, (_, i) => (buttonSeat + 1 + i) % n);
};
/** Seats ordered starting immediately left of the button (postflop-acting order), for odd-chip
 *  distribution at split pots (ENGINE_DESIGN.md §2.1: "odd chip to first seat left of button"). */
export const orderSeatsFromButton = (seats, buttonSeat, n) => {
    const distance = (seat) => (seat - buttonSeat - 1 + n) % n;
    return seats.slice().sort((a, b) => distance(a) - distance(b));
};
//# sourceMappingURL=positions.js.map